'use client';

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Upload,
  FileText,
  Mic,
  Scissors,
  Download,
  ChevronRight
} from 'lucide-react';

const steps = [
  {
    icon: Upload,
    title: 'Screen record or Upload your Screen',
    description:
      'Import your screen recording or upload a captured video directly. VideoMule works with recordings from any screen recorder or capture tool.',
    videoUrl:
      'https://gw-ai-social-media.b-cdn.net/VideoMule/step1%20(1).mp4',
    videoLabel: 'See how uploading works'
  },
  {
    icon: FileText,
    title: 'AI generates the script',
    description:
      'VideoMule analyzes your video and automatically creates a clear, step-by-step narration script based on what happens on screen.',
    videoUrl:
      'https://gw-ai-social-media.b-cdn.net/VideoMule/step2%20(1).mp4',
    videoLabel: 'Watch AI script generation'
  },
  {
    icon: Mic,
    title: 'Choose a professional voice',
    description:
      'Select a natural-sounding AI voice from our library. Choose the language, accent, and style that best fits your video.',
    videoUrl:
      'https://gw-ai-social-media.b-cdn.net/VideoMule/step3%20(1).mp4',
    videoLabel: 'Hear voice options'
  },
  {
    icon: Scissors,
    title: 'Get finished video (with voice & edit done)',
    description:
      'Our multi-track editor syncs video, voiceover, and captions automatically. Make quick edits or adjustments before exporting.',
    videoUrl:
      'https://gw-ai-social-media.b-cdn.net/VideoMule/step4%20(1).mp4',
    videoLabel: 'See the editor in action'
  },
  {
    icon: Download,
    title: 'Export your tutorial video',
    description:
      'Preview the final result and export your tutorial video with AI narration in high quality.',
    videoUrl:
      'https://gw-ai-social-media.b-cdn.net/VideoMule/step4%20(1).mp4',
    videoLabel: 'Watch the export process'
  }
];

export default function HowItWorks() {
  const [activeStep, setActiveStep] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const resetAutoAdvance = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 5000);
  };

  useEffect(() => {
    resetAutoAdvance();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handleManualClick = (i: number) => {
    setActiveStep(i);
    resetAutoAdvance();
  };

  return (
    <section
      id="how-it-works"
      className="py-14 sm:py-20 md:py-24 bg-background"
    >
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <motion.div
          className="text-center mb-8 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-flex items-center rounded-full border border-primary/20 px-3 py-1 text-xs font-medium text-primary mb-4 bg-videomule-light-green dark:bg-primary/10">
            How it Works
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-foreground">
            Five steps to your video
          </h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto text-sm sm:text-base md:text-lg">
            From raw screen capture to polished tutorial in minutes, not hours.
          </p>
        </motion.div>
        <motion.div
          className="grid lg:grid-cols-[420px_1fr] gap-6 lg:gap-12 items-start"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="space-y-2 sm:space-y-3 lg:min-h-[520px]">
            {steps.map((step, i) => {
              const isActive = activeStep === i;
              const isPast = i < activeStep;
              const Icon = step.icon;
              return (
                <button
                  key={i}
                  onClick={() => handleManualClick(i)}
                  className={`w-full text-left px-4 sm:px-5 py-4 sm:py-5 rounded-xl transition-all duration-300 relative ${
                    isActive
                      ? 'bg-card-green shadow-md border border-videomule-green/20'
                      : 'hover:bg-muted/50 border border-transparent'
                  }`}
                >
                  <div className="flex items-start gap-3 sm:gap-5">
                    <div
                      className={`relative z-10 shrink-0 w-7 h-7 sm:w-[30px] sm:h-[30px] rounded-full flex items-center justify-center text-xs sm:text-[13px] font-bold transition-all duration-300 ${
                        isActive
                          ? 'bg-videomule-green text-white ring-4 ring-videomule-green/20 scale-110'
                          : isPast
                            ? 'bg-videomule-green/80 text-white'
                            : 'bg-muted text-muted-foreground border border-border'
                      }`}
                    >
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon
                          className={`h-4 w-4 shrink-0 transition-colors duration-300 ${
                            isActive
                              ? 'text-videomule-green'
                              : 'text-muted-foreground'
                          }`}
                        />
                        <h3
                          className={`font-semibold text-sm sm:text-base md:text-lg leading-snug transition-colors duration-300 ${
                            isActive
                              ? 'text-foreground'
                              : isPast
                                ? 'text-foreground/70'
                                : 'text-muted-foreground'
                          }`}
                        >
                          {step.title}
                        </h3>
                      </div>
                      <AnimatePresence initial={false}>
                        {isActive && (
                          <motion.div
                            initial={{ opacity: 0, height: 0, overflow: 'hidden' }}
                            animate={{ opacity: 1, height: 'auto', overflow: 'hidden' }}
                            exit={{ opacity: 0, height: 0, overflow: 'hidden' }}
                            transition={{
                              duration: 0.3,
                              ease: [0.4, 0, 0.2, 1]
                            }}
                          >
                            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed pr-2 sm:pr-4 mt-1">
                              {step.description}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    {isActive && (
                      <motion.div
                        initial={{ opacity: 0, x: -4 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="mt-1"
                      >
                        <ChevronRight className="h-5 w-5 text-primary shrink-0" />
                      </motion.div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="lg:sticky lg:top-24">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="rounded-2xl border border-border overflow-hidden bg-card shadow-sm"
              >
                <div className="aspect-video w-full bg-muted relative">
                  <video
                    key={steps[activeStep].videoUrl}
                    src={steps[activeStep].videoUrl}
                    className="w-full h-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                  />
                </div>
                <div className="px-4 sm:px-5 py-3 sm:py-4 flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground line-clamp-2">
                      Step {activeStep + 1}: {steps[activeStep].title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {steps[activeStep].videoLabel}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full font-medium shrink-0 ml-3">
                    {activeStep + 1} / {steps.length}
                  </span>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
